import React from 'react'
import { MapPin, User, Calendar, Clock, DollarSign, Users, CheckCircle } from 'lucide-react'
import { useThemeColors } from '../../hooks/useThemeColors'
import { format } from 'date-fns'

interface CommandConfirmationProps {
  type: string
  data: Record<string, unknown>
  onConfirm: () => void
  onCancel: () => void
  isConfirming?: boolean
}

export function CommandConfirmation({
  type,
  data,
  onConfirm,
  onCancel,
  isConfirming
}: CommandConfirmationProps) {
  const { colors } = useThemeColors()

  if (type === 'create_booking' && data.booking) {
    const booking = data.booking
    return (
      <div
        className="rounded-lg border p-4"
        style={{
          borderColor: colors.border,
          backgroundColor: `${colors.backgroundLight}80`
        }}
      >
        <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
          <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
          Confirm Booking
        </h3>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>{booking.facilityName}</span>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>
              {booking.customerName}
              {booking.isNewCustomer && (
                <span
                  className="ml-2 text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.primary, color: 'white' }}
                >
                  NEW
                </span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>
              {booking.startDateTime
                ? format(new Date(booking.startDateTime), 'EEEE, MMMM d, yyyy')
                : booking.date}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>
              {booking.startTime} - {booking.endTime} ({booking.durationMinutes} min)
            </span>
          </div>

          {booking.price > 0 && (
            <div className="flex items-center gap-3">
              <DollarSign className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
              <span style={{ color: colors.text }}>${booking.price.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            disabled={isConfirming}
            className="flex-1 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: colors.primary }}
          >
            {isConfirming ? 'Creating...' : 'Confirm'}
          </button>
          <button
            onClick={onCancel}
            disabled={isConfirming}
            className="px-4 py-2 rounded-lg font-medium border transition-colors"
            style={{ borderColor: colors.border, color: colors.textSecondary }}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if ((type === 'create_class_registration' || type === 'add_to_waitlist') && data.registration) {
    const reg = data.registration
    return (
      <div
        className="rounded-lg border p-4"
        style={{
          borderColor: colors.border,
          backgroundColor: `${colors.backgroundLight}80`
        }}
      >
        <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
          <Users className="w-5 h-5" style={{ color: colors.primary }} />
          {reg.willBeWaitlisted ? 'Add to Waitlist' : 'Confirm Registration'}
        </h3>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>{reg.className}</span>
          </div>

          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>
              {reg.classTime ? format(new Date(reg.classTime), 'EEEE, MMMM d @ h:mm a') : 'Time TBD'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>{reg.participantName}</span>
          </div>

          <div className="flex items-center gap-3">
            <Users className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>
              Capacity: {reg.currentCapacity}
              {reg.willBeWaitlisted && (
                <span className="ml-2 text-yellow-500">(Full - Will be waitlisted)</span>
              )}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            disabled={isConfirming}
            className="flex-1 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: reg.willBeWaitlisted ? '#EAB308' : colors.primary }}
          >
            {isConfirming ? 'Processing...' : reg.willBeWaitlisted ? 'Add to Waitlist' : 'Register'}
          </button>
          <button
            onClick={onCancel}
            disabled={isConfirming}
            className="px-4 py-2 rounded-lg font-medium border transition-colors"
            style={{ borderColor: colors.border, color: colors.textSecondary }}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (type === 'cancel_booking' && data.booking) {
    const booking = data.booking
    return (
      <div
        className="rounded-lg border p-4"
        style={{
          borderColor: colors.border,
          backgroundColor: `${colors.backgroundLight}80`
        }}
      >
        <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
          <CheckCircle className="w-5 h-5" style={{ color: colors.error }} />
          Confirm Cancellation
        </h3>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>{booking.title}</span>
          </div>

          {booking.customerName && (
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
              <span style={{ color: colors.text }}>{booking.customerName}</span>
            </div>
          )}

          {booking.facilityName && (
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
              <span style={{ color: colors.text }}>{booking.facilityName}</span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 flex-shrink-0" style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.text }}>
              {booking.startTime
                ? format(new Date(booking.startTime), 'EEEE, MMMM d @ h:mm a')
                : 'Time not specified'}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            disabled={isConfirming}
            className="flex-1 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: colors.error }}
          >
            {isConfirming ? 'Cancelling...' : 'Cancel Booking'}
          </button>
          <button
            onClick={onCancel}
            disabled={isConfirming}
            className="px-4 py-2 rounded-lg font-medium border transition-colors"
            style={{ borderColor: colors.border, color: colors.textSecondary }}
          >
            Keep Booking
          </button>
        </div>
      </div>
    )
  }

  // Generic confirmation
  return (
    <div
      className="rounded-lg border p-4"
      style={{
        borderColor: colors.border,
        backgroundColor: `${colors.backgroundLight}80`
      }}
    >
      <h3 className="font-semibold mb-4" style={{ color: colors.text }}>
        Confirm Action
      </h3>
      <p className="mb-4" style={{ color: colors.textSecondary }}>
        {data.message || 'Ready to proceed?'}
      </p>
      <div className="flex gap-2">
        <button
          onClick={onConfirm}
          disabled={isConfirming}
          className="flex-1 py-2 rounded-lg font-medium text-white"
          style={{ backgroundColor: colors.primary }}
        >
          {isConfirming ? 'Processing...' : 'Confirm'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg font-medium border"
          style={{ borderColor: colors.border, color: colors.textSecondary }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
